import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pazientiAPI } from '../../services/api';
import PazienteForm from './PazienteForm';
import ModulisticaWizard from './ModulisticaWizard';
import NotaInline from './NotaInline';
import { useAuthStore } from '../../store/useStore';
import { Search, Clock, Download, Contact, FilePlus2, Droplet, Pencil, Trash2 } from 'lucide-react';
import '../../styles/Pazienti.css';
import { notificaErrore, messaggioDa } from '../../utils/notifiche';

const PazientiList = () => {
    const [pazienti, setPazienti] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [selectedPaziente, setSelectedPaziente] = useState(null);
    const [showModulo, setShowModulo] = useState(false);
    const [modalitaModulo, setModalitaModulo] = useState('completo');
    const [soloLongevity, setSoloLongevity] = useState(false);
    const [pagina, setPagina] = useState(1);
    const [totale, setTotale] = useState(0);
    const PER_PAGINA = 100;
    const navigate = useNavigate();

    // L'esportazione dell'intera anagrafica e' riservata agli admin:
    // i pulsanti restano nascosti agli altri, che altrimenti
    // riceverebbero un errore di permessi senza capire perche'.
    const isAdmin = useAuthStore((state) => state.user)?.ruolo === 'admin';

    useEffect(() => {
        loadPazienti();
        // eslint-disable-next-line
    }, [search, soloLongevity, pagina]);

    /* Cercando si riparte dalla prima pagina: restare alla ventesima
       con tre risultati confonde. */
    useEffect(() => { setPagina(1); }, [search, soloLongevity]);

    const loadPazienti = async () => {
        try {
            setLoading(true);
            const parametri = {
                search,
                limit: PER_PAGINA,
                offset: (pagina - 1) * PER_PAGINA,
            };
            if (soloLongevity) parametri.longevity = true;
            const response = await pazientiAPI.getAll(parametri);
            if (response.data.success) {
                setPazienti(response.data.data.pazienti || []);
                setTotale(response.data.data.total || 0);
            }
        } catch (error) {
            console.error('Errore caricamento pazienti:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            const response = await pazientiAPI.exportCSV();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `pazienti-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Export CSV error:', error); notificaErrore(messaggioDa(error, 'Non riesco a esportare il CSV'));
        }
    };

    const handleExportVCard = async () => {
        try {
            const response = await pazientiAPI.exportVCard();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `pazienti-${new Date().toISOString().split('T')[0]}.vcf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            notificaErrore(messaggioDa(error, 'Non riesco a esportare la vCard'));
        }
    };

    const handleDelete = async (e, p) => {
        e.stopPropagation();
        const msg =
            `Sei sicuro di voler eliminare il paziente ${p.nome} ${p.cognome}?\n\n` +
            `Verranno eliminati DEFINITIVAMENTE anche tutte le sue foto, documenti, ` +
            `procedure, anamnesi e alert.\n\n` +
            `Una volta eliminato NON sarà più recuperabile.`;
        if (window.confirm(msg)) {
            try {
                await pazientiAPI.delete(p.id);
                loadPazienti();
            } catch (error) {
                notificaErrore(messaggioDa(error, 'Non riesco a eliminare il paziente'));
            }
        }
    };

    const handleEdit = (e, paziente) => {
        e.stopPropagation();
        setSelectedPaziente(paziente);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setSelectedPaziente(null);
    };

    const handleFormSuccess = () => {
        loadPazienti();
    };

    return (
        <div className="pazienti-container">
            <div className="pazienti-header">
                <h1>Pazienti</h1>
                <div className="header-actions">
                    <input
                        type="text"
                        placeholder="Nome, cognome o codice fiscale"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="search-input"
                    />
                    <button
                        onClick={() => setSoloLongevity((v) => !v)}
                        className={`btn-secondary ${soloLongevity ? 'btn-filtro-attivo' : ''}`}
                        title={soloLongevity ? 'Mostra tutti i pazienti' : 'Mostra solo i pazienti del percorso Longevity'}
                    >
                        <Clock size={15} strokeWidth={1.8} aria-hidden="true" />
                        {soloLongevity ? 'Solo Longevity' : 'Longevity'}
                    </button>
                    {isAdmin && (
                        <>
                            <button onClick={handleExportCSV} className="btn-secondary">
                                <Download size={15} strokeWidth={1.8} aria-hidden="true" />
                                CSV
                            </button>
                            <button onClick={handleExportVCard} className="btn-secondary">
                                <Contact size={15} strokeWidth={1.8} aria-hidden="true" />
                                VCard
                            </button>
                        </>
                    )}
                    {/* L'accettazione e' l'unica strada per creare un
                        paziente: passa dai moduli e dalle firme, mentre il
                        vecchio «Nuovo paziente» inseriva un'anagrafica nuda
                        senza consensi. Il modulo resta raggiungibile per
                        MODIFICARE un paziente esistente, dall'icona della
                        riga. */}
                    <button onClick={() => { setModalitaModulo('completo'); setShowModulo(true); }} className="btn-primary">
                        <FilePlus2 size={15} strokeWidth={1.8} aria-hidden="true" />
                        Accettazione paziente
                    </button>
                    <button onClick={() => { setModalitaModulo('prelievi'); setShowModulo(true); }} className="btn-secondary btn-prelievi">
                        <Droplet size={15} strokeWidth={1.8} aria-hidden="true" />
                        Accettazione punto prelievi
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="loading">Caricamento...</div>
            ) : (
                <div className="table-container">
                    <table className="pazienti-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Nome</th>
                                <th>Cognome</th>
                                <th>Codice fiscale</th>
                                <th>Telefono</th>
                                <th>Email</th>
                                <th>Note</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pazienti.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{textAlign: 'center', padding: '40px'}}>
                                        {search ? 'Nessun paziente trovato' : 'Nessun paziente. Clicca "Accettazione paziente" per registrarne uno.'}
                                    </td>
                                </tr>
                            ) : (
                                pazienti.map((p) => (
                                    <tr key={p.id} onClick={() => navigate(`/pazienti/${p.id}`)} style={{cursor: 'pointer'}}>
                                        <td className="cella-id">
                                            <span className="pid">{p.id_paziente}</span>
                                            <span className="simboli-paziente">
                                                {p.tipo_accettazione === 'prelievi' && (
                                                    <span className="rm-stato rm-stato-attenzione" title="Consensi non completi">Prelievi</span>
                                                )}
                                                {p.longevity === true && (
                                                    <span className="rm-stato rm-stato-accento">Longevity</span>
                                                )}
                                            </span>
                                        </td>
                                        <td className="cella-nome">{p.nome}</td>
                                        <td>{p.cognome}</td>
                                        <td>{p.codice_fiscale}</td>
                                        <td>{p.telefono || '-'}</td>
                                        <td>{p.email || '-'}</td>
                                        <td className="cella-note" onClick={(e) => e.stopPropagation()}>
                                            <NotaInline
                                                pazienteId={p.id}
                                                valoreIniziale={p.note}
                                                compatta
                                                onSaved={(nuovo) => { p.note = nuovo; }}
                                            />
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button onClick={(e) => handleEdit(e, p)} className="btn-edit" title="Modifica"><Pencil size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                                                <button onClick={(e) => handleDelete(e, p)} className="btn-delete" title="Elimina"><Trash2 size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {totale > PER_PAGINA && (
                        <div className="paz-pagine">
                            <button disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
                                ‹ Precedente
                            </button>
                            <span>
                                {(pagina - 1) * PER_PAGINA + 1}–{Math.min(pagina * PER_PAGINA, totale)}
                                {' di '}{totale.toLocaleString('it-IT')}
                            </span>
                            <button disabled={pagina * PER_PAGINA >= totale} onClick={() => setPagina(pagina + 1)}>
                                Successiva ›
                            </button>
                        </div>
                    )}
                </div>
            )}

            {showForm && (
                <PazienteForm
                    paziente={selectedPaziente}
                    onClose={handleCloseForm}
                    onSuccess={handleFormSuccess}
                />
            )}
            {showModulo && (
                <ModulisticaWizard
                    modalita={modalitaModulo}
                    onClose={() => setShowModulo(false)}
                    onSuccess={loadPazienti}
                />
            )}
        </div>
    );
};

export default PazientiList;
